import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { checkAuthenticatedAdmin } from '../utils/client-functions';
import { setFetching } from '../redux/states';
import { useErrorSetter } from '../hooks/useUiMessages';
import TransactionsTable from '../components/UI/Tables/TransactionsTable';
import styles from './Analytics.module.css';
import { Grid, Card, CardContent, Typography, Box, Select, MenuItem, TextField, Button, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const TransactionsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(500);
  const [loading, setLoading] = useState(false);

  // Sorting (client-side visual sort)
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Filters
  const [status, setStatus] = useState('');
  const [product, setProduct] = useState('');
  const [currency, setCurrency] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [query, setQuery] = useState('');

  const isAdminAuth = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
  const errorSetter = useErrorSetter();
  const [currentAdminRole, setCurrentAdminRole] = useState('');

  // Keep newest first at the API (already sorted desc by createdAt); also allow local sort toggle
  const sortedTransactions = useMemo(() => {
    const rows = [...transactions];
    const val = (r) => {
      // Normalize createdAt to comparable value
      if (sortBy === 'createdAt') return r.createdAt;
      return r[sortBy] ?? '';
    };
    rows.sort((a, b) => {
      const A = val(a);
      const B = val(b);
      if (A === B) return 0;
      if (sortDir === 'asc') return A > B ? 1 : -1;
      return A < B ? 1 : -1;
    });
    return rows;
  }, [transactions, sortBy, sortDir]);

  const handleSortChange = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  // Sync filters in URL (optional next step)
  useEffect(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (product) params.set('product', product);
    if (currency) params.set('currency', currency);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (query) params.set('q', query);
    params.set('page', String(page));
    params.set('limit', String(limit));
    params.set('sortBy', String(sortBy));
    params.set('sortDir', String(sortDir));
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', url);
  }, [status, product, currency, startDate, endDate, query, page, limit, sortBy, sortDir]);

  useEffect(() => {
    // fetch current admin role for export gating
    (async () => {
      try {
        const resp = await axios.get('/origin/get-current-admin', { headers: { 'x-access-token': isAdminAuth } });
        setCurrentAdminRole(resp?.data?.role || '');
      } catch {}
    })();
    const urlParams = new URLSearchParams(window.location.search);
    const s = urlParams.get('status');
    const pr = urlParams.get('product');
    const cu = urlParams.get('currency');
    const sd = urlParams.get('startDate');
    const ed = urlParams.get('endDate');
    const q = urlParams.get('q');
    const pg = parseInt(urlParams.get('page') || '1', 10);
    const lm = parseInt(urlParams.get('limit') || '500', 10);
    const sb = urlParams.get('sortBy');
    const sdv = urlParams.get('sortDir');
    if (s) setStatus(s);
    if (pr) setProduct(pr);
    if (cu) setCurrency(cu);
    if (sd) setStartDate(sd);
    if (ed) setEndDate(ed);
    if (q) setQuery(q);
    if (!Number.isNaN(pg)) setPage(pg);
    if (!Number.isNaN(lm)) setLimit(lm);
    if (sb) setSortBy(sb);
    if (sdv === 'asc' || sdv === 'desc') setSortDir(sdv);
    // run once on mount to hydrate from URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTx = async () => {
      try {
        await checkAuthenticatedAdmin();
      } catch (error) {
        return navigate('/');
      }

      try {
        dispatch(setFetching(true));
        setLoading(true);
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (product) params.append('product', product);
        if (currency) params.append('currency', currency);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (query) params.append('q', query);
        params.append('page', String(page));
        params.append('limit', String(limit));

        const response = await axios.get(`/origin/get-transactions?${params.toString()}`, {
          headers: { 'x-access-token': isAdminAuth },
        });

        if (response.status === 200) {
          setTransactions(response.data.transactions);
          setTotal(response.data.total || response.data.transactions?.length || 0);
          setPage(response.data.page || 1);
          setLimit(response.data.limit || limit);
        }
      } catch (error) {
        errorSetter(error?.response?.data?.message || 'Failed to fetch transactions');
      } finally {
        dispatch(setFetching(false));
        setLoading(false);
      }
    };

    fetchTx();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, product, currency, startDate, endDate, query, page, limit]);

  // Derived KPIs (from loaded data only)
  const kpis = useMemo(() => {
    const count = transactions.length;
    const totalAmount = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const totalSettled = transactions.reduce((sum, t) => sum + (Number(t.amount_settled) || 0), 0);
    const successCount = transactions.filter(
      (t) => (t.status || '').toLowerCase() === 'successful' || t.completed === true
    ).length;
    const failureCount = transactions.filter((t) => (t.status || '').toLowerCase() === 'failed').length;
    return { count, totalAmount, totalSettled, successCount, failureCount };
  }, [transactions]);

  const resetFilters = () => {
    setStatus('');
    setProduct('');
    setCurrency('');
    setStartDate('');
    setEndDate('');
    setQuery('');
    setPage(1);
  };

  const downloadCsv = () => {
    const headers = [
      'tx_ref',
      'transaction_id',
      'status',
      'completed',
      'amount',
      'amount_settled',
      'app_fee',
      'currency',
      'payment_type',
      'duration',
      'product',
      'couponCode',
      'ip',
      'customer_email',
      'customer_fullName',
      'createdAt',
    ];

    const rows = transactions.map((t) => [
      t.tx_ref,
      t.transaction_id,
      t.status,
      t.completed,
      t.amount,
      t.amount_settled,
      t.app_fee,
      t.currency,
      t.payment_type,
      t.duration,
      t.product,
      t.couponCode,
      t.ip,
      t?.customer?.email,
      t?.customer?.fullName,
      t.createdAt,
    ]);

    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((v) => {
            const s = v === undefined || v === null ? '' : String(v);
            if (s.includes('"') || s.includes(',') || s.includes('\n')) {
              return '"' + s.replace(/"/g, '""') + '"';
            }
            return s;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `transactions_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.analyticsContainer}>
      <AdminSideMenu />
      <div className={styles.analyticsContent}>
        {/* Header */}
        <Box className={styles.analyticsHeader}>
          <Box className={styles.headerLeft}>
            <Typography variant="h4" className={styles.pageTitle}>
              Transactions Dashboard
            </Typography>
            <Typography variant="subtitle1" className={styles.pageSubtitle}>
              Monitor payments with flexible filters and export
            </Typography>
          </Box>
          <Box className={styles.headerRight}></Box>
        </Box>

        {/* Filters toolbar (top-right, just before table) */}
        <Box display="flex" justifyContent="flex-end" gap={2} mb={2} sx={{ backgroundColor: '#fff', p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <TextField
            size="small"
            placeholder="Search ref, email, IP..."
            value={query}
            onChange={(e) => {
              setPage(1);
              setQuery(e.target.value);
            }}
          />
          <TextField
            size="small"
            placeholder="Product"
            value={product}
            onChange={(e) => {
              setPage(1);
              setProduct(e.target.value);
            }}
          />
          <Select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
            size="small"
            className={styles.dateRangeSelect}
            displayEmpty
          >
            <MenuItem value="">
              <em>Status</em>
            </MenuItem>
            <MenuItem value="successful">Successful</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
          <Select
            value={currency}
            onChange={(e) => {
              setPage(1);
              setCurrency(e.target.value);
            }}
            size="small"
            className={styles.dateRangeSelect}
            displayEmpty
          >
            <MenuItem value="">
              <em>Currency</em>
            </MenuItem>
            <MenuItem value="NGN">NGN</MenuItem>
            <MenuItem value="USD">USD</MenuItem>
            <MenuItem value="EUR">EUR</MenuItem>
          </Select>
          <TextField
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => {
              setPage(1);
              setStartDate(e.target.value);
            }}
          />
          <TextField
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => {
              setPage(1);
              setEndDate(e.target.value);
            }}
          />
          <Button variant="outlined" onClick={resetFilters}>
            Reset
          </Button>
          {currentAdminRole === 'superadmin' && (
            <Button variant="contained" color="primary" onClick={downloadCsv} startIcon={<DownloadIcon />}>
              Export CSV
            </Button>
          )}
        </Box>

        {/* KPIs */}
        <Grid container spacing={2} className={styles.kpiContainer}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.metricCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Rows Loaded
                </Typography>
                <Typography variant="h4" className={styles.metricValue}>
                  {kpis.count}
                </Typography>
                <Chip size="small" label={`Total: ${total}`} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.metricCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Amount (gross)
                </Typography>
                <Typography variant="h4" className={styles.metricValue}>
                  {kpis.totalAmount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.metricCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Amount Settled
                </Typography>
                <Typography variant="h4" className={styles.metricValue}>
                  {kpis.totalSettled.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.metricCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Success / Failed
                </Typography>
                <Typography variant="h5" className={styles.metricValue}>
                  {kpis.successCount} / {kpis.failureCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table */}
        <div style={{ marginTop: 8 }}>
          <TransactionsTable
            allTransactions={sortedTransactions}
            loading={loading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={handleSortChange}
          />
        </div>

        {/* Pagination controls (optional next step) */}
        <Box display="flex" justifyContent="flex-end" alignItems="center" backgroundColor="white" gap={2} mt={2}>
          <Typography variant="body2">Page {page}</Typography>
          <Button variant="outlined" size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
          <Button variant="outlined" size="small" disabled={(page * limit) >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          <Select size="small" value={limit} onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={250}>250</MenuItem>
            <MenuItem value={500}>500</MenuItem>
            <MenuItem value={1000}>1000</MenuItem>
          </Select>
        </Box>
      </div>
    </div>
  );
};

export default TransactionsDashboard;
